import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Archive,
  ArrowDownToLine,
  ArrowUpRight,
  Check,
  ChevronDown,
  Clock3,
  Download,
  Eye,
  EyeOff,
  FileDown,
  Heart,
  KeyRound,
  LogIn,
  LogOut,
  Play,
  Radio,
  RefreshCw,
  Shield,
  SkipBack,
  SkipForward,
  Square,
  Upload,
  X,
} from "lucide-react";
import type { AdminPlaylistSummary, NeteaseTrackMetadata, Playlist, PlaylistDraft, PlaylistStatus, PlaylistSummary, PreviewResponse, Track } from "@shared/types";
import {
  changeAdminPassword,
  adminLogin,
  adminLogout,
  getAdminPlaylists,
  getAdminSession,
  getNeteaseArtwork,
  getPlaylist,
  getPlaylists,
  inspectNeteaseTracks,
  previewMarkdown,
  publishMarkdown,
  setPlaylistStatus,
} from "./lib/api";
import {
  readFavorites,
  readLastDuration,
  readTimerStart,
  writeFavorites,
  writeLastDuration,
  writeTimerStart,
} from "./lib/localState";
import { buildPlaylistHref, readPlaylistSlug } from "./lib/playlistUrl";
import { resolvedTrackLabel, trackIdentityStatus } from "./lib/trackIdentity";
import { canAttemptNeteaseLogin } from "./lib/neteaseRecovery";

const formatDate = new Intl.DateTimeFormat("zh-CN", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const maxUploadBytes = 1024 * 1024;
const frontendMetadataTtlMs = 5 * 60 * 1000;
const maxFrontendMetadataEntries = 4;
type PlaylistHistoryMode = "push" | "replace" | "none";
type FrontendMetadataCache = Map<string, { metadata: NeteaseTrackMetadata; expiresAt: number }>;

function cachedTrackMetadata(cache: FrontendMetadataCache, trackId: string): NeteaseTrackMetadata | null {
  const entry = cache.get(trackId);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    cache.delete(trackId);
    return null;
  }
  cache.delete(trackId);
  cache.set(trackId, entry);
  return entry.metadata;
}

function rememberTrackMetadata(
  cache: FrontendMetadataCache,
  trackId: string,
  metadata: NeteaseTrackMetadata,
): void {
  cache.delete(trackId);
  while (cache.size >= maxFrontendMetadataEntries) {
    const oldestTrackId = cache.keys().next().value as string | undefined;
    if (!oldestTrackId) break;
    cache.delete(oldestTrackId);
  }
  cache.set(trackId, { metadata, expiresAt: Date.now() + frontendMetadataTtlMs });
}

function syncPlaylistUrl(slug: string | null, mode: Exclude<PlaylistHistoryMode, "none">): void {
  const href = buildPlaylistHref(
    window.location.pathname,
    window.location.search,
    window.location.hash,
    slug,
  );
  window.history[mode === "push" ? "pushState" : "replaceState"](null, "", href);
}

function clock(value: number): string {
  const total = Math.max(0, Math.floor(value / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return [hours, minutes, seconds].map((item) => String(item).padStart(2, "0")).join(":");
}

function playbackLabel(metadata: NeteaseTrackMetadata | null): string {
  if (!metadata) return "STATUS UNKNOWN";
  if (metadata.restrictionReason === "vip") return "VIP / LIMITED";
  if (metadata.restrictionReason === "purchase") return "PAID / LIMITED";
  if (metadata.playbackStatus === "unavailable") return "UNAVAILABLE";
  if (metadata.playbackStatus === "playable") return "LIKELY PLAYABLE";
  return "STATUS UNKNOWN";
}

function playbackTitle(metadata: NeteaseTrackMetadata | null): string {
  if (!metadata) return "未能确认网易云播放权限";
  if (metadata.restrictionReason === "vip") return "网易云标记为 VIP 内容，iframe 可能只能试听或无法播放";
  if (metadata.restrictionReason === "purchase") return "网易云标记为付费专辑内容，iframe 可能无法完整播放";
  if (metadata.playbackStatus === "unavailable") return "网易云元数据显示当前曲目可能无版权或不可播放";
  if (metadata.playbackStatus === "playable") return "元数据显示可能可播放，最终状态仍以网易云播放器为准";
  return "网易云未返回足够的权限信息";
}

function slugify(value: string): string {
  return value.replace(/[^a-z0-9\u4e00-\u9fff]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
}

function downloadFile(filename: string, contents: string, type: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function markdownCell(value: string): string {
  return value.replace(/\|/g, "\\|").replace(/[\r\n]+/g, " ").trim();
}

function playlistMarkdown(playlist: Playlist, favorites: Set<string>): string {
  const includeTrackIds = !playlist.neteaseId && playlist.tracks.some((track) => track.neteaseTrackId);
  const headers = ["红心", "歌名", "歌手", ...(includeTrackIds ? ["网易云歌曲ID"] : [])];
  const rows = playlist.tracks.map((track) => [
    favorites.has(track.favoriteKey) ? "x" : "",
    markdownCell(track.title),
    markdownCell(track.artist),
    ...(includeTrackIds ? [track.neteaseTrackId || ""] : []),
  ]);
  const table = [
    `| ${headers.join(" | ")} |`,
    `| ${headers.map(() => "---").join(" | ")} |`,
    ...rows.map((row) => `| ${row.join(" | ")} |`),
  ].join("\n");
  const metadata = [
    "---",
    `title: ${JSON.stringify(playlist.title)}`,
    `date: ${JSON.stringify(playlist.date)}`,
    `data: ${JSON.stringify(playlist.data)}`,
    ...(playlist.description ? [`description: ${JSON.stringify(playlist.description)}`] : []),
    ...(playlist.neteaseId ? [`neteaseId: ${JSON.stringify(playlist.neteaseId)}`] : []),
    "---",
  ].join("\n");
  return `${metadata}\n\n${table}\n`;
}

function playlistJson(playlist: Playlist, favorites: Set<string>): string {
  return `${JSON.stringify(
    {
      title: playlist.title,
      date: playlist.date,
      data: playlist.data,
      description: playlist.description,
      neteaseId: playlist.neteaseId,
      tracks: playlist.tracks.map((track) => ({
        title: track.title,
        artist: track.artist,
        favorite: favorites.has(track.favoriteKey),
        neteaseTrackId: track.neteaseTrackId,
      })),
    },
    null,
    2,
  )}\n`;
}

function ToolButton({ label, children, onClick, disabled = false }: {
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button className="icon-button" type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function Header({ onArchive, onRefresh, archiveOpen, admin = false }: {
  onArchive?: () => void;
  onRefresh?: () => void;
  archiveOpen?: boolean;
  admin?: boolean;
}) {
  return (
    <header className="site-header">
      <a className="brand" href={admin ? "/" : "#top"}>
        <span className="brand-mark" aria-hidden="true">FM</span>
        <span>FANCYM <span className="brand-subtitle"><em>/</em> DAILY LISTENING</span></span>
      </a>
      <div className="header-meta">
        <span className="status-dot"><i /> SYSTEM ONLINE</span>
        {onRefresh && <ToolButton label="刷新歌单" onClick={onRefresh}><RefreshCw size={16} /></ToolButton>}
        {onArchive && (
          <button className={`header-link ${archiveOpen ? "active" : ""}`} type="button" onClick={onArchive}>
            <Archive size={15} /> ARCHIVE <ChevronDown size={13} />
          </button>
        )}
        <a className="header-link" href={admin ? "/" : "/admin"}>
          {admin ? <ArrowUpRight size={15} /> : <Shield size={15} />} {admin ? "PUBLIC" : "ADMIN"}
        </a>
      </div>
    </header>
  );
}

function FittedTitle({ children }: { children: string }) {
  const titleRef = useRef<HTMLHeadingElement>(null);

  useLayoutEffect(() => {
    const title = titleRef.current;
    const container = title?.parentElement;
    if (!title || !container) return;
    let frame = 0;
    let active = true;
    const fit = () => {
      title.style.fontSize = "";
      const computed = window.getComputedStyle(title);
      const maxSize = Number.parseFloat(computed.getPropertyValue("--title-max-size")) || 62;
      const minSize = Number.parseFloat(computed.getPropertyValue("--title-min-size")) || 30;
      const maxLines = Number.parseInt(computed.getPropertyValue("--title-max-lines"), 10) || 3;
      let low = Math.ceil(minSize);
      let high = Math.floor(maxSize);
      let best = low;
      while (low <= high) {
        const size = Math.floor((low + high) / 2);
        title.style.fontSize = `${size}px`;
        const lineHeight = Number.parseFloat(window.getComputedStyle(title).lineHeight) || size;
        if (title.scrollHeight <= lineHeight * maxLines + 2 && title.scrollWidth <= title.clientWidth + 1) {
          best = size;
          low = size + 1;
        } else {
          high = size - 1;
        }
      }
      title.style.fontSize = `${best}px`;
    };
    const scheduleFit = () => {
      if (!active) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(fit);
    };
    let observedWidth = -1;
    const observer = new ResizeObserver(([entry]) => {
      const nextWidth = entry.contentRect.width;
      if (Math.abs(nextWidth - observedWidth) < 0.5) return;
      observedWidth = nextWidth;
      scheduleFit();
    });
    observer.observe(container);
    scheduleFit();
    void document.fonts?.ready.then(scheduleFit);
    return () => {
      active = false;
      observer.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [children]);

  return <h1 ref={titleRef}>{children}</h1>;
}

type MachineSlotStyle = React.CSSProperties & {
  "--machine-scale": number;
  "--machine-height": string;
};

function ResponsiveMachine({ children }: { children: React.ReactNode }) {
  const slotRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    const observer = new ResizeObserver(([entry]) => {
      const nextScale = Math.min(1, Math.max(0.44, entry.contentRect.width / 600));
      setScale((current) => Math.abs(current - nextScale) < 0.005 ? current : nextScale);
    });
    observer.observe(slot);
    return () => observer.disconnect();
  }, []);

  const style: MachineSlotStyle = {
    "--machine-scale": scale,
    "--machine-height": `${Math.ceil(530 * scale)}px`,
  };
  return <div className="machine-slot" ref={slotRef} style={style}>{children}</div>;
}

function MachineVisual({ playlist, selectedTrack, artworkUrl, favorite, onFavorite, hasPrevious, hasNext, onPrevious, onNext, timerRunning, onTimerToggle }: {
  playlist: Playlist | null;
  selectedTrack: Track | null;
  artworkUrl: string | null;
  favorite: boolean;
  onFavorite: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  timerRunning: boolean;
  onTimerToggle: () => void;
}) {
  const [loadedArtworkUrl, setLoadedArtworkUrl] = useState<string | null>(null);
  const [failedArtworkUrl, setFailedArtworkUrl] = useState<string | null>(null);
  const artworkReady = !!artworkUrl && loadedArtworkUrl === artworkUrl && failedArtworkUrl !== artworkUrl;
  const favoriteLabel = selectedTrack
    ? `${favorite ? "取消红心" : "添加红心"}：${selectedTrack.title}`
    : "没有可收藏的曲目";

  return (
    <div className={`machine-visual ${timerRunning ? "timer-running" : ""}`} aria-label="FANCYM listening machine">
      <div className="machine-shadow" />
      <div className="machine-plate plate-yellow"><span>{playlist?.data || playlist?.date || "WAITING"}</span></div>
      <div className="machine-arm" />
      <div className="machine-disc">
        <div className={`disc-label ${artworkReady ? "has-artwork" : ""}`}>
          <div className="disc-label-fallback"><Radio size={23} /><span>FM</span></div>
          {artworkUrl && failedArtworkUrl !== artworkUrl && (
            <img
              className={`disc-artwork ${artworkReady ? "loaded" : ""}`}
              src={artworkUrl}
              alt={`${selectedTrack?.title || "当前歌曲"}封面`}
              referrerPolicy="no-referrer"
              onLoad={() => setLoadedArtworkUrl(artworkUrl)}
              onError={() => setFailedArtworkUrl(artworkUrl)}
            />
          )}
        </div>
      </div>
      <div className="machine-base" aria-label="曲目导航">
        <button className="base-control" type="button" disabled={!hasPrevious} onClick={onPrevious} aria-label="上一首" title="上一首">
          <SkipBack size={16} fill="currentColor" />
        </button>
        <span className="base-index" aria-live="polite">{selectedTrack ? `${String(selectedTrack.position).padStart(2, "0")} / ${String(playlist?.tracks.length || 0).padStart(2, "0")}` : "-- / --"}</span>
        <button className="base-control" type="button" disabled={!hasNext} onClick={onNext} aria-label="下一首" title="下一首">
          <SkipForward size={16} fill="currentColor" />
        </button>
      </div>
      <div className="machine-signal signal-blue">{String(playlist?.tracks.length || 0).padStart(2, "0")}</div>
      <button
        className={`machine-signal signal-red ${favorite ? "liked" : ""}`}
        type="button"
        disabled={!selectedTrack}
        aria-label={favoriteLabel}
        aria-pressed={selectedTrack ? favorite : undefined}
        title={favoriteLabel}
        onClick={onFavorite}
      >
        <Heart size={19} fill={favorite ? "currentColor" : "none"} />
      </button>
      <button
        className={`machine-signal signal-black ${timerRunning ? "running" : ""}`}
        type="button"
        aria-label={timerRunning ? "结束聆听计时" : "开始聆听计时"}
        aria-pressed={timerRunning}
        title={timerRunning ? "结束聆听计时" : "开始聆听计时"}
        onClick={onTimerToggle}
      >
        {timerRunning ? <Square size={17} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
      </button>
      <div className="machine-caption">
        <span>{selectedTrack ? `SELECTED / ${String(selectedTrack.position).padStart(2, "0")}` : "NO TRACK SELECTED"}</span>
        <strong>{selectedTrack?.title || "LISTEN / ARCHIVE / REPEAT"}</strong>
      </div>
    </div>
  );
}

type SessionTimer = {
  running: boolean;
  duration: number;
  lastDuration: number | null;
  start: () => void;
  end: () => void;
};

function useSessionTimer(): SessionTimer {
  const [startedAt, setStartedAt] = useState<number | null>(() => readTimerStart());
  const [elapsed, setElapsed] = useState(() => {
    const started = readTimerStart();
    return started ? Date.now() - started : 0;
  });
  const [lastDuration, setLastDuration] = useState<number | null>(() => readLastDuration());

  useEffect(() => {
    if (!startedAt) return;
    const update = () => setElapsed(Date.now() - startedAt);
    update();
    const interval = window.setInterval(update, 1000);
    return () => window.clearInterval(interval);
  }, [startedAt]);

  const start = useCallback(() => {
    if (startedAt) return;
    const now = Date.now();
    writeTimerStart(now);
    setStartedAt(now);
    setLastDuration(null);
  }, [startedAt]);

  const end = useCallback(() => {
    if (!startedAt) return;
    const duration = Date.now() - startedAt;
    writeTimerStart(null);
    writeLastDuration(duration);
    setLastDuration(duration);
    setStartedAt(null);
    setElapsed(duration);
  }, [startedAt]);

  return {
    running: startedAt !== null,
    duration: startedAt ? elapsed : lastDuration || 0,
    lastDuration,
    start,
    end,
  };
}

function TimerPanel({ timer, onStart }: { timer: SessionTimer; onStart: () => void }) {
  const { running, duration, lastDuration, end } = timer;

  return (
    <section className={`timer-panel ${running ? "running" : ""}`} aria-label="聆听计时">
      <div className="timer-heading"><Clock3 size={15} /> SESSION TIMER <span>{running ? "LIVE" : "READY"}</span></div>
      <div className="timer-value">{clock(duration)}</div>
      <div className="timer-actions">
        {!running ? (
          <button className="action-button action-yellow" type="button" onClick={onStart}><Play size={15} fill="currentColor" /> START SESSION</button>
        ) : (
          <button className="action-button action-red" type="button" onClick={end}><Square size={13} fill="currentColor" /> END SESSION</button>
        )}
        {lastDuration !== null && !running && <span className="last-session">LAST / {clock(lastDuration)}</span>}
      </div>
    </section>
  );
}

function TrackList({ playlist, favorites, selectedTrackId, onSelect, onFavorite }: {
  playlist: Playlist;
  favorites: Set<string>;
  selectedTrackId: string | null;
  onSelect: (trackId: string) => void;
  onFavorite: (key: string) => void;
}) {
  return (
    <section className="track-section" id="tracks">
      <div className="section-kicker"><span>02 / TRACK INDEX</span><span>{playlist.tracks.length} ITEMS</span></div>
      <div className="track-list">
        {playlist.tracks.map((track) => (
          <div className={`track-row ${track.id === selectedTrackId ? "selected" : ""}`} key={track.id}>
            <button
              className="track-select"
              type="button"
              aria-label={`选择 ${track.title}`}
              aria-pressed={track.id === selectedTrackId}
              onClick={() => onSelect(track.id)}
            >
              <span className="track-number">{String(track.position).padStart(2, "0")}</span>
              <span className="track-name">{track.title}</span>
              <span className="track-artist">{track.artist}</span>
            </button>
            <button
              className={`heart-button ${favorites.has(track.favoriteKey) ? "liked" : ""}`}
              type="button"
              aria-label={favorites.has(track.favoriteKey) ? `取消收藏 ${track.title}` : `收藏 ${track.title}`}
              title={favorites.has(track.favoriteKey) ? "取消红心" : "加红心"}
              onClick={() => {
                onSelect(track.id);
                onFavorite(track.favoriteKey);
              }}
            ><Heart size={17} fill={favorites.has(track.favoriteKey) ? "currentColor" : "none"} /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

function EmptyState({ onRefresh }: { onRefresh: () => void }) {
  return (
    <main className="empty-state">
      <div className="empty-symbol"><Radio size={36} /></div>
      <p className="eyebrow">FANCYM / SIGNAL LOST</p>
      <h1>今日歌单尚未发布</h1>
      <p className="empty-note">NO PUBLISHED SIGNAL</p>
      <button className="action-button action-blue" type="button" onClick={onRefresh}><RefreshCw size={15} /> REFRESH</button>
    </main>
  );
}

function ArchiveDrawer({ playlists, activeSlug, onSelect, onClose }: {
  playlists: PlaylistSummary[];
  activeSlug: string | null;
  onSelect: (slug: string) => void;
  onClose: () => void;
}) {
  return (
    <aside className="archive-drawer" aria-label="历史歌单">
      <div className="drawer-head"><span>ARCHIVE / INDEX</span><ToolButton label="关闭歌单档案" onClick={onClose}><X size={17} /></ToolButton></div>
      <div className="drawer-rule" />
      {playlists.length === 0 ? <p className="drawer-empty">NO ARCHIVE</p> : playlists.map((item, index) => (
        <button className={`archive-item ${item.slug === activeSlug ? "selected" : ""}`} key={item.id} type="button" onClick={() => onSelect(item.slug)}>
          <span className="archive-index">{String(index + 1).padStart(2, "0")}</span>
          <span><strong>{item.date}</strong><small>{item.title}</small></span>
          <span className="archive-count">{String(item.trackCount).padStart(2, "0")}</span>
        </button>
      ))}
    </aside>
  );
}

function PlayerPage() {
  const [playlists, setPlaylists] = useState<PlaylistSummary[]>([]);
  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(() => readFavorites());
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [autoPlayTrackId, setAutoPlayTrackId] = useState<string | null>(null);
  const [autoPlayPlaylistId, setAutoPlayPlaylistId] = useState<string | null>(null);
  const [playerRequestId, setPlayerRequestId] = useState(0);
  const [loadedSourceKey, setLoadedSourceKey] = useState<string | null>(null);
  const [trackMetadata, setTrackMetadata] = useState<NeteaseTrackMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const loadRequestId = useRef(0);
  const metadataCache = useRef<FrontendMetadataCache>(new Map());
  const timer = useSessionTimer();

  const load = useCallback(async (requestedSlug: string | null, historyMode: PlaylistHistoryMode) => {
    const requestId = ++loadRequestId.current;
    setLoading(true);
    setError("");
    try {
      const response = await getPlaylists();
      const nextSlug = requestedSlug && response.playlists.some((item) => item.slug === requestedSlug)
        ? requestedSlug
        : response.playlists[0]?.slug || null;
      const nextPlaylist = nextSlug ? (await getPlaylist(nextSlug)).playlist : null;
      if (requestId !== loadRequestId.current) return;
      setPlaylists(response.playlists);
      setActiveSlug(nextSlug);
      setPlaylist(nextPlaylist);
      setSelectedTrackId(null);
      setAutoPlayTrackId(null);
      setAutoPlayPlaylistId(null);
      const resolvedHistoryMode = historyMode === "none" && requestedSlug !== nextSlug
        ? "replace"
        : historyMode;
      if (resolvedHistoryMode !== "none") syncPlaylistUrl(nextSlug, resolvedHistoryMode);
    } catch (requestError) {
      if (requestId !== loadRequestId.current) return;
      setError(requestError instanceof Error ? requestError.message : "加载失败");
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(readPlaylistSlug(window.location.search), "replace");
    const handlePopState = () => {
      void load(readPlaylistSlug(window.location.search), "none");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [load]);

  const selectPlaylist = (slug: string) => {
    setArchiveOpen(false);
    void load(slug, slug === activeSlug ? "replace" : "push");
  };

  const toggleFavorite = (key: string) => {
    const next = new Set(favorites);
    if (next.has(key)) next.delete(key); else next.add(key);
    setFavorites(next);
    writeFavorites(next);
  };

  const selectedTrack = useMemo(() => {
    if (!playlist) return null;
    const selected = playlist.tracks.find((track) => track.id === selectedTrackId);
    if (selected) return selected;
    if (!playlist.neteaseId) {
      return playlist.tracks.find((track) => track.neteaseTrackId) || playlist.tracks[0] || null;
    }
    return playlist.tracks[0] || null;
  }, [playlist, selectedTrackId]);
  const selectedTrackIndex = selectedTrack ? playlist?.tracks.findIndex((track) => track.id === selectedTrack.id) ?? -1 : -1;
  const artworkTrackId = playlist?.neteaseId ? null : selectedTrack?.neteaseTrackId || null;
  const nextArtworkTrackId = !playlist?.neteaseId && selectedTrackIndex >= 0
    ? playlist?.tracks[selectedTrackIndex + 1]?.neteaseTrackId || null
    : null;

  useEffect(() => {
    const cachedMetadata = artworkTrackId
      ? cachedTrackMetadata(metadataCache.current, artworkTrackId)
      : null;
    setTrackMetadata(cachedMetadata);
    setMetadataLoading(!!artworkTrackId && !cachedMetadata);
    if (!artworkTrackId) return;
    if (cachedMetadata) return;
    const controller = new AbortController();
    let active = true;
    void getNeteaseArtwork(artworkTrackId, controller.signal)
      .then((response) => {
        if (active) {
          rememberTrackMetadata(metadataCache.current, artworkTrackId, response);
          setTrackMetadata(response);
        }
      })
      .catch((requestError: unknown) => {
        if (active && !(requestError instanceof DOMException && requestError.name === "AbortError")) {
          setTrackMetadata(null);
        }
      })
      .finally(() => {
        if (active) setMetadataLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [artworkTrackId]);

  useEffect(() => {
    if (!nextArtworkTrackId || nextArtworkTrackId === artworkTrackId) return;
    const controller = new AbortController();
    let prefetchedImage: HTMLImageElement | null = null;
    const timeout = window.setTimeout(() => {
      const cachedMetadata = cachedTrackMetadata(metadataCache.current, nextArtworkTrackId);
      const request = cachedMetadata
        ? Promise.resolve(cachedMetadata)
        : getNeteaseArtwork(nextArtworkTrackId, controller.signal).then((metadata) => {
            rememberTrackMetadata(metadataCache.current, nextArtworkTrackId, metadata);
            return metadata;
          });
      void request.then((metadata) => {
        if (!metadata.artworkUrl || controller.signal.aborted) return;
        prefetchedImage = new Image();
        prefetchedImage.decoding = "async";
        prefetchedImage.src = metadata.artworkUrl;
      }).catch(() => undefined);
    }, 300);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
      prefetchedImage?.removeAttribute("src");
    };
  }, [artworkTrackId, nextArtworkTrackId]);

  const hasPrevious = selectedTrackIndex > 0;
  const hasNext = selectedTrackIndex >= 0 && selectedTrackIndex < (playlist?.tracks.length || 0) - 1;
  const selectRelativeTrack = (offset: number) => {
    if (!playlist || selectedTrackIndex < 0) return;
    const nextTrack = playlist.tracks[selectedTrackIndex + offset];
    if (!nextTrack) return;
    setSelectedTrackId(nextTrack.id);
    setAutoPlayTrackId(!playlist.neteaseId && nextTrack.neteaseTrackId ? nextTrack.id : null);
  };
  const neteaseSource = playlist?.neteaseId
    ? {
        mode: "playlist" as const,
        label: "PLAYLIST",
        title: `${playlist.title} 网易云歌单播放器`,
        url: `https://music.163.com/outchain/player?type=0&id=${encodeURIComponent(playlist.neteaseId)}&auto=${autoPlayPlaylistId === playlist.neteaseId ? "1" : "0"}&height=430`,
      }
    : selectedTrack?.neteaseTrackId
      ? {
          mode: "track" as const,
          label: `TRACK / ${String(selectedTrack.position).padStart(2, "0")}`,
          title: `${selectedTrack.title} 网易云单曲播放器`,
          url: `https://music.163.com/outchain/player?type=2&id=${encodeURIComponent(selectedTrack.neteaseTrackId)}&auto=${autoPlayTrackId === selectedTrack.id ? "1" : "0"}&height=66`,
        }
      : null;
  const sourceRenderKey = neteaseSource ? `${neteaseSource.url}#${playerRequestId}` : "";
  const sourceLoading = !!neteaseSource && loadedSourceKey !== sourceRenderKey;
  const officialNeteaseUrl = playlist?.neteaseId
    ? `https://music.163.com/#/playlist?id=${encodeURIComponent(playlist.neteaseId)}`
    : selectedTrack?.neteaseTrackId
      ? `https://music.163.com/#/song?id=${encodeURIComponent(selectedTrack.neteaseTrackId)}`
      : null;
  const sourceStatus = sourceLoading
    ? "LOADING"
    : neteaseSource?.mode === "track"
      ? metadataLoading ? "CHECKING" : playbackLabel(trackMetadata)
      : neteaseSource?.label || "PENDING";
  const loginRecoveryAvailable = neteaseSource?.mode === "track"
    && !metadataLoading
    && canAttemptNeteaseLogin(trackMetadata);
  const reloadNeteasePlayer = () => {
    if (!neteaseSource) return;
    setLoadedSourceKey(null);
    setPlayerRequestId((value) => value + 1);
  };
  const startSessionAndPlayer = () => {
    timer.start();
    if (playlist?.neteaseId) setAutoPlayPlaylistId(playlist.neteaseId);
    else if (selectedTrack?.neteaseTrackId) setAutoPlayTrackId(selectedTrack.id);
    if (neteaseSource) setPlayerRequestId((value) => value + 1);
  };
  const favoriteCount = useMemo(() => playlist?.tracks.filter((track) => favorites.has(track.favoriteKey)).length || 0, [favorites, playlist]);

  return (
    <div className="app-shell" id="top">
      <Header onArchive={() => setArchiveOpen((value) => !value)} onRefresh={() => void load(activeSlug, "replace")} archiveOpen={archiveOpen} />
      {archiveOpen && <ArchiveDrawer playlists={playlists} activeSlug={activeSlug} onSelect={selectPlaylist} onClose={() => setArchiveOpen(false)} />}
      {error && <div className="toast toast-error"><AlertTriangle size={16} /> {error} <ToolButton label="关闭提示" onClick={() => setError("")}><X size={14} /></ToolButton></div>}
      {loading && !playlist ? <div className="loading-state"><RefreshCw className="spin" size={18} /> LOADING SIGNAL</div> : !playlist ? <EmptyState onRefresh={() => void load(activeSlug, "replace")} /> : (
        <>
          <main className="player-stage">
            <div className="stage-topline"><span>01 / NOW PLAYING</span><span>SHANGHAI / {formatDate.format(new Date())}</span></div>
            <div className="stage-copy">
              <p className="eyebrow">DAILY LISTENING / {playlist.date}</p>
              <FittedTitle>{playlist.title}</FittedTitle>
              <p className="stage-description">{playlist.description || "A small signal for the day."}</p>
            </div>
            <ResponsiveMachine>
              <MachineVisual
                playlist={playlist}
                selectedTrack={selectedTrack}
                artworkUrl={trackMetadata?.artworkUrl || null}
                favorite={!!selectedTrack && favorites.has(selectedTrack.favoriteKey)}
                onFavorite={() => {
                  if (selectedTrack) toggleFavorite(selectedTrack.favoriteKey);
                }}
                hasPrevious={hasPrevious}
                hasNext={hasNext}
                onPrevious={() => selectRelativeTrack(-1)}
                onNext={() => selectRelativeTrack(1)}
                timerRunning={timer.running}
                onTimerToggle={timer.running ? timer.end : startSessionAndPlayer}
              />
            </ResponsiveMachine>
            <div className={`player-embed ${neteaseSource?.mode === "track" ? "track-source" : "playlist-source"}`}>
              <div className="embed-head">
                <span><Radio size={14} /> NETEASE SOURCE</span>
                <div className="embed-actions">
                  <span
                    className={`embed-status ${neteaseSource?.mode === "track" && !metadataLoading ? trackMetadata?.playbackStatus || "unknown" : ""}`}
                    title={neteaseSource?.mode === "track" ? playbackTitle(trackMetadata) : undefined}
                  >{(sourceLoading || metadataLoading) && <RefreshCw className="spin" size={11} />}{sourceStatus}</span>
                  {officialNeteaseUrl && (
                    <a className="embed-external" href={officialNeteaseUrl} target="_blank" rel="noreferrer" aria-label="在网易云音乐打开" title="在网易云音乐打开">
                      <ArrowUpRight size={14} />
                    </a>
                  )}
                </div>
              </div>
              {loginRecoveryAvailable && officialNeteaseUrl && (
                <div className="embed-recovery" role="group" aria-label="网易云登录与重载">
                  <a className="embed-recovery-action" href={officialNeteaseUrl} target="_blank" rel="noreferrer">
                    <LogIn size={13} /> NETEASE LOGIN
                  </a>
                  <button className="embed-recovery-action" type="button" onClick={reloadNeteasePlayer} disabled={sourceLoading}>
                    <RefreshCw size={13} className={sourceLoading ? "spin" : ""} /> RELOAD PLAYER
                  </button>
                </div>
              )}
              {neteaseSource ? (
                <iframe
                  key={sourceRenderKey}
                  title={neteaseSource.title}
                  loading="lazy"
                  allow="autoplay; encrypted-media"
                  src={neteaseSource.url}
                  onLoad={() => setLoadedSourceKey(sourceRenderKey)}
                />
              ) : <div className="embed-placeholder"><Radio size={20} /><span>NETEASE / PENDING</span><small>{playlist.neteaseId ? "PLAYLIST ID REQUIRED" : "TRACK ID REQUIRED"}</small></div>}
            </div>
            <div className="stage-data"><span><b>DATE</b>{playlist.date}</span><span><b>TRACKS</b>{String(playlist.tracks.length).padStart(2, "0")}</span><span><b>LIKED</b>{String(favoriteCount).padStart(2, "0")}</span></div>
          </main>
          <div className="control-band">
            <TimerPanel timer={timer} onStart={startSessionAndPlayer} />
            <div className="export-panel">
              <div className="timer-heading"><Download size={15} /> EXPORT CURRENT LIST</div>
              <div className="export-actions">
                <button className="action-button action-blue" type="button" onClick={() => downloadFile(`${playlist.date}-${slugify(playlist.title)}.md`, playlistMarkdown(playlist, favorites), "text/markdown") }><FileDown size={15} /> MARKDOWN</button>
                <button className="action-button action-black" type="button" onClick={() => downloadFile(`${playlist.date}-${slugify(playlist.title)}.json`, playlistJson(playlist, favorites), "application/json") }><ArrowDownToLine size={15} /> JSON</button>
              </div>
            </div>
          </div>
          <TrackList
            playlist={playlist}
            favorites={favorites}
            selectedTrackId={selectedTrack?.id || null}
            onSelect={(trackId) => {
              setSelectedTrackId(trackId);
              if (trackId !== selectedTrack?.id) setAutoPlayTrackId(null);
            }}
            onFavorite={toggleFavorite}
          />
          <footer className="site-footer"><span>FANCYM / DAILY LISTENING</span><span>苏ICP备2026022259号-1</span><a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">MIIT <ArrowUpRight size={12} /></a><span>© {new Date().getFullYear()}</span></footer>
        </>
      )}
    </div>
  );
}

function LoginPanel({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try { await adminLogin(password); onLogin(); }
    catch (requestError) { setError(requestError instanceof Error ? requestError.message : "登录失败"); }
  };
  return (
    <main className="admin-login">
      <div className="admin-login-mark"><Shield size={28} /></div>
      <p className="eyebrow">FANCYM / PRIVATE CONSOLE</p>
      <h1>ADMIN ACCESS</h1>
      <form onSubmit={submit} className="login-form">
        <label htmlFor="password">PASSWORD</label>
        <input id="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus autoComplete="current-password" />
        <button className="action-button action-yellow" type="submit"><LogIn size={15} /> ENTER CONSOLE</button>
        {error && <p className="form-error"><AlertTriangle size={14} /> {error}</p>}
      </form>
    </main>
  );
}

function PasswordPanel() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage("");
    if (newPassword.length < 12) {
      setMessage("新密码至少需要 12 个字符");
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage("两次输入的新密码不一致");
      return;
    }
    setSaving(true);
    try {
      await changeAdminPassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage("PASSWORD UPDATED / 密码已更新");
    } catch (requestError) {
      setMessage(requestError instanceof Error ? requestError.message : "修改失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="security-section">
      <div className="section-kicker"><span>ACCOUNT / SECURITY</span><span>PRIVATE</span></div>
      <div className="security-grid">
        <div>
          <div className="security-title"><KeyRound size={18} /><h2>UPDATE PASSWORD</h2></div>
          <p className="security-note">修改后立即生效，服务器只保存不可逆的密码哈希。</p>
        </div>
        <form className="password-form" onSubmit={submit}>
          <label htmlFor="current-password">CURRENT PASSWORD</label>
          <input id="current-password" type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" required />
          <label htmlFor="new-password">NEW PASSWORD</label>
          <input id="new-password" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={12} autoComplete="new-password" required />
          <label htmlFor="confirm-password">CONFIRM NEW PASSWORD</label>
          <input id="confirm-password" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={12} autoComplete="new-password" required />
          <button className="action-button action-yellow" type="submit" disabled={saving}><KeyRound size={15} /> {saving ? "UPDATING" : "UPDATE PASSWORD"}</button>
          {message && <p className={message.startsWith("PASSWORD UPDATED") ? "form-success" : "form-error"}>{message.startsWith("PASSWORD UPDATED") ? <Check size={14} /> : <AlertTriangle size={14} />} {message}</p>}
        </form>
      </div>
    </section>
  );
}

function DraftPreview({ draft, neteaseTracks, checking }: {
  draft: PlaylistDraft;
  neteaseTracks?: Record<string, NeteaseTrackMetadata>;
  checking: boolean;
}) {
  const checkedTracks = Object.values(neteaseTracks || {});
  const restrictedCount = checkedTracks.filter((item) => item.playbackStatus === "restricted" || item.playbackStatus === "unavailable").length;
  const identityStatuses = draft.neteaseId
    ? []
    : draft.tracks.flatMap((track) => {
        if (!track.neteaseTrackId) return [];
        return [trackIdentityStatus(track, neteaseTracks?.[track.neteaseTrackId] || null)];
      });
  const matchedCount = identityStatuses.filter((status) => status === "match").length;
  const mismatchCount = identityStatuses.filter((status) => status === "mismatch").length;
  const unknownCount = identityStatuses.filter((status) => status === "unknown").length;
  return (
    <div className="draft-preview">
      <div className="preview-meta"><span>{draft.date}</span><strong>{draft.title}</strong><span>{draft.tracks.length} TRACKS</span></div>
      <div className="preview-source preview-data"><span>DISPLAY DATA</span><code>{draft.data}</code></div>
      <div className="preview-source">
        <span><Radio size={13} /> {draft.neteaseId ? "NETEASE PLAYLIST" : "NETEASE TRACK IDS"}</span>
        <code>{draft.neteaseId || `${draft.tracks.filter((track) => track.neteaseTrackId).length}/${draft.tracks.length}`}</code>
      </div>
      {!draft.neteaseId && draft.tracks.some((track) => track.neteaseTrackId) && (
        <>
          <div className="preview-source preview-inspection">
            <span><Shield size={13} /> PLAYABILITY CHECK</span>
            <code>{checking ? "CHECKING" : `${restrictedCount} LIMITED / ${checkedTracks.length} CHECKED`}</code>
          </div>
          <div className="preview-source preview-inspection">
            <span><Check size={13} /> CONTENT MATCH</span>
            <code>{checking ? "CHECKING" : `${matchedCount} MATCH / ${mismatchCount} REVIEW / ${unknownCount} UNKNOWN`}</code>
          </div>
        </>
      )}
      {(draft.errors.length > 0 || draft.warnings.length > 0) && <div className="parse-notes">
        {draft.errors.map((item) => <p className="form-error" key={item}><AlertTriangle size={14} /> {item}</p>)}
        {draft.warnings.map((item) => <p className="form-warning" key={item}><AlertTriangle size={14} /> {item}</p>)}
      </div>}
      <div className="preview-tracks">{draft.tracks.map((track) => {
        const metadata = track.neteaseTrackId ? neteaseTracks?.[track.neteaseTrackId] || null : null;
        const status = draft.neteaseId ? "PLAYLIST" : !track.neteaseTrackId ? "NO ID" : checking ? "CHECKING" : playbackLabel(metadata);
        const tone = metadata?.playbackStatus || "unknown";
        const identity = track.neteaseTrackId ? trackIdentityStatus(track, metadata) : "unknown";
        const identityLabel = checking
          ? "CHECKING"
          : identity === "match"
            ? "ID MATCH"
            : identity === "mismatch"
              ? "CHECK NAME"
              : "NOT VERIFIED";
        return <div key={`${track.position}-${track.title}`}>
          <span className="preview-index">{String(track.position).padStart(2, "0")}</span>
          <b>{track.title}</b>
          <small>{track.artist}</small>
          <code className="preview-track-id">{draft.neteaseId ? "—" : track.neteaseTrackId || "—"}</code>
          <span className="preview-checks">
            <span className={`preview-playback ${tone}`} title={track.neteaseTrackId ? playbackTitle(metadata) : undefined}>
              {metadata?.playbackStatus === "playable" ? <Check size={12} /> : <AlertTriangle size={12} />}{status}
            </span>
            {!draft.neteaseId && track.neteaseTrackId && (
              <span className={`preview-identity ${identity}`} title={resolvedTrackLabel(metadata)}>
                {identity === "match" ? <Check size={12} /> : <AlertTriangle size={12} />}{identityLabel}
              </span>
            )}
          </span>
        </div>;
      })}</div>
    </div>
  );
}

function AdminPage() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewChecking, setPreviewChecking] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [managedPlaylists, setManagedPlaylists] = useState<AdminPlaylistSummary[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const refreshManagedPlaylists = useCallback(async () => {
    try { setManagedPlaylists((await getAdminPlaylists()).playlists); }
    catch { setManagedPlaylists([]); }
  }, []);

  const check = useCallback(async () => {
    try { setAuthenticated((await getAdminSession()).authenticated); }
    catch { setAuthenticated(false); }
  }, []);
  useEffect(() => { void check(); }, [check]);
  useEffect(() => {
    if (authenticated) void refreshManagedPlaylists();
  }, [authenticated, refreshManagedPlaylists]);

  if (authenticated === null) return <div className="loading-state"><RefreshCw className="spin" size={18} /> CHECKING ACCESS</div>;
  if (!authenticated) return <div className="app-shell"><Header admin /><LoginPanel onLogin={() => setAuthenticated(true)} /></div>;

  const chooseFile = (next: File | null) => {
    setPreview(null);
    setPreviewChecking(false);
    if (next && !/\.md$/i.test(next.name)) {
      setFile(null);
      setMessage("只支持 .md 文件");
      return;
    }
    if (next && next.size > maxUploadBytes) {
      setFile(null);
      setMessage("文件不能超过 1 MB");
      return;
    }
    setFile(next);
    setMessage("");
  };
  const previewFile = async () => {
    if (!file) return;
    setSubmitting(true); setMessage("");
    try {
      const parsed = await previewMarkdown(file);
      setPreview(parsed);
      const trackIds = parsed.draft.neteaseId
        ? []
        : parsed.draft.tracks.flatMap((track) => track.neteaseTrackId ? [track.neteaseTrackId] : []);
      if (trackIds.length) {
        setPreviewChecking(true);
        try {
          const inspected = await inspectNeteaseTracks(trackIds);
          setPreview({ ...parsed, neteaseTracks: inspected.tracks });
        } catch {
          const unknown = Object.fromEntries([...new Set(trackIds)].map((trackId) => [trackId, {
            artworkUrl: null,
            playbackStatus: "unknown" as const,
            restrictionReason: null,
            resolvedTitle: null,
            resolvedArtists: [],
          }]));
          setPreview({ ...parsed, neteaseTracks: unknown });
        } finally {
          setPreviewChecking(false);
        }
      }
    }
    catch (requestError) { setMessage(requestError instanceof Error ? requestError.message : "解析失败"); }
    finally { setSubmitting(false); }
  };
  const publishFile = async () => {
    if (!file || !preview) return;
    setSubmitting(true); setMessage("");
    try {
      await publishMarkdown(file, preview.previewToken);
      setMessage("PUBLISHED / 歌单已发布");
      setPreview(null); setFile(null);
      await refreshManagedPlaylists();
    } catch (requestError) { setMessage(requestError instanceof Error ? requestError.message : "发布失败"); }
    finally { setSubmitting(false); }
  };
  const logout = async () => { await adminLogout(); setAuthenticated(false); };
  const updatePlaylistStatus = async (slug: string, status: PlaylistStatus) => {
    setSubmitting(true);
    try {
      await setPlaylistStatus(slug, status);
      await refreshManagedPlaylists();
      setMessage(status === "published" ? "RESTORED / 歌单已重新发布" : "ARCHIVED / 歌单已下架");
    } catch (requestError) {
      setMessage(requestError instanceof Error ? requestError.message : "状态更新失败");
    } finally {
      setSubmitting(false);
    }
  };
  const publishedCount = managedPlaylists.filter((item) => item.status === "published").length;
  const messageSuccess = /^(PUBLISHED|ARCHIVED|RESTORED)/.test(message);

  return (
    <div className="app-shell admin-shell">
      <Header admin />
      <main className="admin-main">
        <div className="admin-title-row"><div><p className="eyebrow">PRIVATE CONSOLE / 01</p><h1>UPLOAD ARCHIVE</h1></div><button className="header-link" type="button" onClick={() => void logout()}><LogOut size={15} /> LOG OUT</button></div>
        <section className="upload-workspace">
          <label
            className={`drop-zone ${file ? "has-file" : ""} ${dragActive ? "drag-active" : ""} ${submitting ? "busy" : ""}`}
            htmlFor="markdown-file"
            aria-disabled={submitting}
            onDragEnter={(event) => { event.preventDefault(); if (!submitting) setDragActive(true); }}
            onDragOver={(event) => { event.preventDefault(); if (!submitting) setDragActive(true); }}
            onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
            onDrop={(event) => {
              event.preventDefault();
              setDragActive(false);
              if (submitting) return;
              chooseFile(event.dataTransfer.files[0] || null);
            }}
          >
            <input id="markdown-file" type="file" accept=".md,text/markdown" disabled={submitting} onChange={(event) => chooseFile(event.target.files?.[0] || null)} />
            {file ? <><Check size={28} /><strong>{file.name}</strong><small>{Math.ceil(file.size / 1024)} KB / READY</small></> : <><Upload size={28} /><strong>DROP OR SELECT MARKDOWN</strong><small>.MD / MAX 1 MB</small></>}
          </label>
          <div className="upload-actions"><button className="action-button action-blue" type="button" disabled={!file || submitting} onClick={() => void previewFile()}><RefreshCw size={15} className={submitting ? "spin" : ""} /> PREVIEW</button><button className="action-button action-yellow" type="button" disabled={!preview || !!preview.draft.errors.length || submitting} onClick={() => void publishFile()}><Upload size={15} /> PUBLISH</button><a className="action-button action-black" href="/playlist-template.md" download><FileDown size={15} /> TEMPLATE</a></div>
          {message && <p className={messageSuccess ? "form-success" : "form-error"}>{messageSuccess ? <Check size={14} /> : <AlertTriangle size={14} />} {message}</p>}
        </section>
        <PasswordPanel />
        {preview && <section className="preview-section"><div className="section-kicker"><span>PARSE PREVIEW / {preview.sourceName}</span><span>UNPUBLISHED</span></div><DraftPreview draft={preview.draft} neteaseTracks={preview.neteaseTracks} checking={previewChecking} /></section>}
        <section className="admin-published">
          <div className="section-kicker"><span>02 / PLAYLIST ARCHIVE</span><span>{publishedCount} LIVE / {managedPlaylists.length - publishedCount} HIDDEN</span></div>
          <div className="published-list">
            {managedPlaylists.length === 0 ? <p className="drawer-empty">NO PLAYLIST</p> : managedPlaylists.map((item) => (
              <div className={`published-row ${item.status}`} key={item.id}>
                <span>{item.date}</span><strong>{item.title}</strong><small>{item.status === "published" ? "LIVE" : "HIDDEN"} / {item.trackCount} TRACKS</small>
                <button
                  className="icon-button"
                  type="button"
                  title={item.status === "published" ? "下架歌单" : "重新发布"}
                  aria-label={`${item.status === "published" ? "下架" : "重新发布"} ${item.title}`}
                  disabled={submitting}
                  onClick={() => void updatePlaylistStatus(item.slug, item.status === "published" ? "hidden" : "published")}
                >{item.status === "published" ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            ))}
          </div>
        </section>
        <section className="format-strip"><span>FORMAT / FLEXIBLE MARKDOWN</span><span>SONG / ARTIST / OPTIONAL NETEASE ID</span><span>PLAYLIST ID TAKES PRIORITY</span></section>
      </main>
      <footer className="site-footer"><span>FANCYM / ADMIN CONSOLE</span><a href="/">PUBLIC VIEW <ArrowUpRight size={12} /></a><span>苏ICP备2026022259号-1</span></footer>
    </div>
  );
}

export default function App() {
  return window.location.pathname.startsWith("/admin") ? <AdminPage /> : <PlayerPage />;
}
